import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { CustomerAddressPanel } from '../components/CustomerAddressPanel.js';
import { deriveIssuingBranchCode } from '../lib/issuing-branch.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { CARD_MAINTENANCE_TEMPLATES } from '../email-templates/templates.js';
import { useContent, useContentMap } from '../content.js';

/**
 * SCRN 49 — Card Maintenance Menu (CCF-87) and SCRN 50 — the card-maintenance
 * actions (CCF-19 PIN reminder, CCF-20 activate, CCF-88 replace damaged,
 * CCF-89 replace lost/stolen, CCF-77 secure address), combined into one screen
 * as in the Figma "Card maintenance menu" frame: the agent picks a card, then a
 * maintenance option, then completes the mandatory scripting + address /
 * secure-address confirmation (+ fee/PIN handling for replacements) before
 * committing the action.
 *
 * Faithful port of the customer's Figma design. The shared agent banner lives in
 * the app shell (AppLayout), so this screen renders only the `<main>` content.
 * Hardcoded sample data is replaced with live data from the Teller-ID-gated BFF
 * via the generated typed client:
 *   - cards:           POST /api/cards
 *   - postal address:  POST /api/cards/postal-address
 *   - secure address:  POST /api/cards/secure-address
 *   - pin reminder:    POST /api/cards/pin-reminder
 *   - activate:        POST /api/cards/activate
 *   - replace:         POST /api/cards/replace
 *   - charge:          POST /api/cards/replacement-charge
 */

/** A single card row (open contract shape; the rendered subset). */
interface CardRow {
  /** The R4 per-card token; every card action keys on it (CCF-87). */
  CardToken: string;
  CardNumber: string;
  CardLast4: string;
  Type: string;
  /** Human-readable card description (R4 `CardDescription`); the Description column. */
  Description?: string;
  Status: string;
  IssueDate: string;
  ExpiryDate: string;
  EarliestActivationDate?: string;
  AccountNumber: string;
  AvailableBalance?: number;
  /** Signed account balance merged by the BFF from customer/accounts/list;
   * negative when overdrawn. Preferred over AvailableBalance for N-hold message
   * selection (issue #173) because the gateway returns AvailableBalance as 0.00
   * for overdrawn accounts.
   */
  AccountBalance?: number;
  AccountHold?: string;
  SecureAddress?: string;
  ChargeExempt?: string;
  /**
   * The embossed name on the card (R4 `card/list` `EmbossingName`, surfaced by
   * the BFF as `EmbossName`) — issue #833. Populated for embossable categories
   * (a "BUS DEBIT CARD"); empty otherwise. Threaded back to `card/replacement`
   * as `embossName` when replacing a BUS DEBIT card, which the gateway requires.
   */
  EmbossName?: string;
}

interface PostalAddressResult {
  /** The postal address lines (R4 `AddressLine1..5`, folded by the BFF adapter). */
  PostalAddress?: string[];
  SecureAddress?: string;
}

/** The four maintenance options offered once a card is selected (CCF-87). */
type Option = '' | 'lost-stolen' | 'damaged' | 'pin-reminder' | 'activation';

/**
 * Issue #173 — upper bound on the 3-hold account-holds read. A holds call that
 * never settles must fail CLOSED (→ `error` + Retry) instead of leaving the
 * commit button stuck forever on "Checking account holds…". Generous enough not
 * to trip on a normally-slow gateway.
 */
const HOLDS_READ_TIMEOUT_MS = 15000;

/**
 * The tri-/quad-state of the fail-CLOSED pre-post holds gate (issue #149/#185/
 * #695). Hoisted to module scope so `HoldGateNotice` + `CardHoldBlockPopup` can
 * type their props against it.
 *   idle    — no fresh holds read has resolved for the current selection
 *   loading — the holds read is in flight (action withheld)
 *   clear   — read succeeded, no `3`/`N` hold (action proceeds)
 *   has3    — read succeeded, `3` hold present (BLOCKED, #185 → pop-up rule 1)
 *   hasN    — read succeeded, `N` hold present (BLOCKED, #695 → pop-up rule 2/3)
 *   error   — read failed (BLOCKED until a successful retry)
 */
type HoldStatus = 'idle' | 'loading' | 'clear' | 'has3' | 'hasN' | 'error';

const OPTION_LABEL: Record<Exclude<Option, ''>, string> = {
  'lost-stolen': 'Replace as lost/stolen',
  damaged: 'Replace as damaged',
  'pin-reminder': 'PIN reminder',
  activation: 'Card activation',
};

/** Normalises a card status to an uppercase token for comparison. */
function normStatus(status?: string): string {
  return (status ?? '').trim().toUpperCase();
}

/** A card whose status is cancelled/closed offers no maintenance actions. */
function isDeadCard(status?: string): boolean {
  const s = normStatus(status);
  return s === 'CANCELLED' || s === 'CANCELED' || s === 'CLOSED';
}

/**
 * Normalise a gateway date to ISO `YYYY-MM-DD` for comparison, tolerant of the
 * two shapes we see: the REAL gateway sends `DD/MM/YYYY` (#356), the mock sends
 * ISO. Empty / `00/00/0000` sentinels (#356) return `''`. Coerces to `String`
 * first so a stray non-string gateway scalar can't throw (#283/#354).
 */
function toIsoDate(value?: string): string {
  const v = String(value ?? '').trim();
  if (!v) return '';
  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (dmy) {
    // `00/00/0000` and other zero sentinels are "no date".
    if (dmy[1] === '00' || dmy[2] === '00' || dmy[3] === '0000') return '';
    return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (iso) {
    if (iso[1] === '0000' || iso[2] === '00' || iso[3] === '00') return '';
    return v;
  }
  return '';
}

/** True when the card is unactivated and there is no *future* earliest-activation
 * date blocking it (CCF-20).
 *
 * #386 — an Unactivated card whose `EarliestActivationDate` is ABSENT or the empty
 * `00/00/0000` sentinel is activatable NOW: the gateway sends the empty sentinel
 * for most cards (its own `card/list` examples show `EarliestActivationDate: ''`)
 * and its `card/activation` op only declines when a *present* earliest date is
 * still in the future — an empty date is not a bar. The old rule required a
 * present, past-or-today date and so silently hid the Activate option for exactly
 * those cards (e.g. the tester's card ending 5534, whose date came back
 * `00/00/0000`). Only a real earliest date that is still in the future gates it. */
function isActivatable(card: CardRow): boolean {
  if (normStatus(card.Status) !== 'UNACTIVATED') return false;
  // #356 — compare in ISO, tolerant of the gateway's DD/MM/YYYY (a DD/MM/YYYY
  // string compared lexically against an ISO `today` was always "greater",
  // silently making cards non-activatable). `toIsoDate` maps an empty value or a
  // `00/00/0000` sentinel to '' — treated here as "no future bar".
  const earliest = toIsoDate(card.EarliestActivationDate);
  if (!earliest) return true;
  const today = new Date().toISOString().slice(0, 10);
  return earliest <= today;
}

/**
 * The maintenance options valid for a card's current status (CCF-19/20/88/89).
 * - Cancelled/closed cards: none (no replace / no maintenance).
 * - Active cards: PIN reminder + replace (damaged / lost-stolen).
 * - Unactivated ("New") cards: replace (damaged / lost-stolen) + activation
 *   (activation unless a *present* EarliestActivationDate is still in the future;
 *   an absent / `00/00/0000` date does not bar it — #386).
 */
function validOptionsFor(card: CardRow): Exclude<Option, ''>[] {
  if (isDeadCard(card.Status)) return [];
  const status = normStatus(card.Status);
  const options: Exclude<Option, ''>[] = [];
  if (status === 'ACTIVE') options.push('pin-reminder');
  // Active + New cards can be replaced (CCF-88/89: only active and new).
  options.push('lost-stolen', 'damaged');
  if (isActivatable(card)) options.push('activation');
  return options;
}

/**
 * Shared style for the primary action buttons (order / activate / replace).
 * Once an action has completed successfully the button is disabled so the same
 * action cannot be submitted twice in a row (CCF-88/89: a card replacement is a
 * one-shot completion — a repeat update is gated by the 24-hour rule, so a second
 * click in the same session is never valid). The disabled state drops the hover /
 * pointer affordance.
 */
const ACTION_BUTTON_CLASS =
  "px-6 py-2 bg-[#FF703E] text-[#1d252c] font-['Arial',sans-serif] font-bold text-[14px] rounded-md transition-all focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20 enabled:cursor-pointer enabled:hover:bg-[#e55a2a] disabled:opacity-50 disabled:cursor-not-allowed";

/**
 * Build a concise inline error string from a failed card-action write response,
 * surfacing the gateway ResultCode + ResultMessage beside the action button so
 * the agent sees WHY it failed and can recover (the gateway-result UX convention
 * — @dylan219 #80/#82/#94; see StandingOrders/Secure-Address). The failure is a
 * non-blocking, retry-able warning, NOT a bare generic toast. A 403 is an
 * RBAC/Teller-ID problem, not a gateway-record outcome, so it keeps its own
 * message. The Code+Message render as `9145 — Unable to Process Transaction`,
 * matching the existing Secure Address surfacing (#236).
 *
 * Issue #248 — the RBAC *action* denial (`requirePermission` refuses a blocked
 * card action with 403 `{ error: 'forbidden' }`, e.g. a restricted role
 * attempting a card write) surfaces @dylan219's authoritative verbatim copy
 * inline beside the button — never a raw 403 or a generic error. `errorBody` is
 * the openapi-fetch parsed error body; only when it is that specific
 * `{ error: 'forbidden' }` shape do we show the action-denied copy. Any OTHER
 * 403 (e.g. the Teller-ID gate's `{ error: 'invalid_teller_id' }`) keeps its own
 * distinct message.
 *
 * Issue #341: this formatter previously only inspected `errorBody` for the
 * `isActionForbidden` 403 shape, never for a TRUE HTTP 4xx gateway rejection.
 * The BFF's global error handler (`server/src/app.ts`) surfaces any such
 * rejection as `{ error: 'gateway_error', message, gateway: { code, message } }`
 * — but that body was never read here, so a true 4xx silently fell through to
 * the generic `fallback` string, swallowing the real reason (reported against
 * card activation, but shared by PIN reminder + replace card too). Mirrors the
 * merge-both-shapes pattern already used by `formatTransferError`
 * (FundsTransferScreen.tsx): prefer the `errorBody.gateway` Code/Message
 * (true 4xx), fall back to the `result.ResultCode`/`ResultMessage` (the
 * HTTP-200-but-business-declined envelope), and only then the generic fallback.
 *
 * Issue #401 — the gateway returns a numeric `ErrorNumber` (e.g. `9184`) with a
 * generic `ErrorMessage` ("Unable to Process Transaction"), so the agent saw
 * "9184 — Unable to Process Transaction" with no indication of WHY the reissue
 * was declined. Per the authoritative catalogue-backing rule (#284/#286 — don't
 * hand-add one code per ticket, resolve the WHOLE op catalogue), the known
 * card-service codes now resolve to their documented reason. `9184` is
 * "Previous update on card for Today" — the card was already updated today, so a
 * further PIN reissue is blocked until the next day (a legitimate gateway
 * business rule, not an app defect). Mirrors `TRANSFER_CODE_REASON`
 * (FundsTransferScreen.tsx). Wording is agent-facing but faithful to
 * requirements/unibanks-reference/API_Error_Codes.csv (the Pin Reissue, Card
 * Replacement and Card Activation entries).
 */
export const CARD_SERVICE_CODE_REASON: Record<string, string> = {
  // Pin Reissue for Debit Card (Unibanks_Pin reissue.yaml)
  '9184':
    'This card was already updated at the bank today — a further update cannot be made until tomorrow. No action is needed; ask the customer to retry after midnight.',
  '9185': 'The card is already being reissued — no further action is needed.',
  '9186': 'A PIN reissue is not allowed for this card type.',
  '338': 'The card is not in a valid status for this action.',
  '9260': 'Card not found.',
  '373': 'The card is not linked to this customer.',
  // Card Activation (Unibanks_CardActivation.yaml). NB the gateway catalogue
  // prefixes 9145/9147/9148 "Technical Error"; #408 (@dylan219) — the plain
  // "the card is not active" read paradoxically on an *activation* journey, so
  // the agent-facing copy makes clear these are bank-side conditions the web
  // app cannot change, and what the agent should do next.
  '9144': 'Card not found — check the card number and try again.',
  '9145':
    'The bank could not activate this card — its status at the bank prevents activation (gateway technical condition). This cannot be corrected from this app; raise it with the card operations team to fix the card record.',
  '9146':
    'This card does not belong to this customer — check you have selected the right card.',
  '9147':
    'This card is not new or replaced, so the bank will not activate it — it may already be active. No activation is needed.',
  '9148':
    'The card cannot be activated yet — its activation date at the bank has not been reached. Ask the customer to try again on or after that date.',
  '9999':
    'A temporary technical error occurred at the bank — please retry in a moment.',
  // Card Replacement (Unibanks_CardReplacement.yaml)
  '345': 'No charge record exists for this card.',
  '454': 'The customer record was not found.',
  '679': 'Invalid card number.',
  '9136': 'A card token is required.',
  '9137': 'The primary account for this card was not found.',
  '9138': 'The primary account for this card does not exist.',
  '9139': 'No customer record was found.',
  '9140': 'The primary account type is invalid.',
  '9141': 'Invalid retain-PIN value.',
  '9142': 'Invalid replacement indicator.',
  '9168': 'An emboss name is required for this card category.',
  '9169': 'This card category cannot be embossed.',
  '9221': 'The card record was not found.',
  // Shared across card-service ops (Pin Reissue / Activation / Replacement)
  '153': 'This function is not available for this channel.',
  '154': 'Invalid channel.',
  '828': 'Invalid Teller Number.',
  '891': 'Invalid Teller Number.',
  '9101': 'The customer record was not found.',
  '9102': 'The card is not linked to this customer.',
  '9103': 'Invalid card number.',
  '9104': 'Invalid card type for this action.',
  '291': 'Invalid card type for this action.',
};

function cardActionErrorText(
  status: number,
  result: { ResultCode?: string; ResultMessage?: string } | undefined,
  fallback: string,
  errorBody?: unknown,
  // Per-code admin-editable advisory overrides (#637 / CCF-113, SCRN-49). When a
  // code is admin-editable via the content catalogue the resolved text (override
  // OR compiled default) wins over the module constant; other codes stay
  // compiled-in via CARD_SERVICE_CODE_REASON.
  overrides: Record<string, string> = {},
): string {
  const blocked = resolveActionBlockMessage(status, errorBody);
  if (blocked !== null) return blocked;
  if (status === 403) return 'Forbidden: check your Teller ID and permissions.';
  const gw = (errorBody ?? {}) as {
    gateway?: { code?: string | number; message?: string };
  };
  const rawCode = gw.gateway?.code ?? result?.ResultCode;
  const code = rawCode === undefined ? '' : String(rawCode).trim();
  const message = (gw.gateway?.message ?? result?.ResultMessage ?? '').trim();
  // Prefer the catalogue reason over the gateway's generic message so the agent
  // sees WHY the action was declined (#401 / #284 / #286), not the bare
  // "NN — Unable to Process Transaction".
  const reason =
    code && code !== '0000'
      ? (overrides[code] ?? CARD_SERVICE_CODE_REASON[code])
      : undefined;
  if (code && reason) return `${code} — ${reason}`;
  if (code && message) return `${code} — ${message}`;
  if (message) return message;
  if (code) return `Gateway code ${code}. Please retry.`;
  return fallback;
}

function formatCurrency(amount?: number, currency = 'EUR'): string {
  if (typeof amount !== 'number') return '';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Issue #695 (@dylan219, authoritative — refines his own #185 3-hold ruling)
 * ============================================================================
 * The Card Maintenance page (SCRN-49) gains three pre-API hold-check rules that
 * fire when the agent attempts Replace lost/stolen, Replace damaged, or PIN
 * reminder, BEFORE the gateway op is triggered. Each surfaces as a MODAL pop-up
 * and BLOCKS the API call:
 *
 *   Rule 1 — a `3` hold on the account (#185): block-until-removed, no proceed,
 *            with a "Recheck holds" action (keeps the #185 semantics).
 *   Rule 2 — an `N` hold (code `N` = "Pay No Withdrawal") AND balance > €0.
 *   Rule 3 — an `N` hold AND balance < €0 (deposit the abs value to clear).
 *
 * Precedence: 3-hold FIRST, then N-hold. The message copy below is the mandated
 * wording — do NOT reword. The 3-hold pop-up wording originated with @dylan219's
 * #695 copy and was refined by tester philipc39 in #753 ("a 3 Hold" — a single
 * named hold state — not "three holds"). It is distinct from the shared
 * `THREE_HOLD_BLOCK_MESSAGE` used by other journeys.
 */

/**
 * Rule 1 — the exact 3-hold pop-up message (@dylan219 #695, wording refined by
 * tester philipc39 in #753). "3 Hold" is a single named account-hold state
 * (HoldCode "3" / StatementOrderAllowed:N, "No Stmts Issued – Check customer
 * address"), NOT a count of three holds — so the copy says "a 3 Hold", not
 * "three holds".
 */
export const CARD_THREE_HOLD_POPUP_MESSAGE =
  'There is currently a 3 Hold on this account. Please verify the ' +
  "customer's address before proceeding.";

/**
 * Rule 2 — the N-hold, POSITIVE-balance pop-up message (@dylan219 #695), with the
 * customer name substituted for `(Customer Name)`.
 */
export function nHoldPositiveMessage(customerName: string): string {
  const name = customerName.trim() || 'there';
  return (
    `Thanks ${name} Due to the status of your account, I will need to contact ` +
    'another department before replacing your card. If you can please provide ' +
    'a contact number and the best time to reach you, I will give you a call ' +
    'back to let you know when this is done'
  );
}

/**
 * Rule 3 — the N-hold, NEGATIVE-balance pop-up message (@dylan219 #695), with the
 * customer name substituted for `(Customer Name)` and `(€XX)` replaced by the
 * amount needed to bring the balance back to €0 = the ABSOLUTE value of the
 * negative balance, formatted as currency (e.g. `€123.45`).
 */
export function nHoldNegativeMessage(
  customerName: string,
  negativeBalance: number,
): string {
  const name = customerName.trim() || 'there';
  const deposit = formatCurrency(Math.abs(negativeBalance));
  return (
    `Thanks ${name} unfortunately I won't be able to process your request at ` +
    'the moment. Your account is currently in a negative balance which has put ' +
    `a hold on it. If you can please deposit ${deposit} to bring it back in ` +
    'credit, once this is done call us back and we will be in a position to ' +
    'assist you and complete your request.'
  );
}

/**
 * Card-type classification (#114). The gateway `CardType` (R4 `card/list`
 * `CardType`, ≤4 chars) is an open-ended code; the values observed across the
 * Unibanks reference + R4 contract are `VSDR` (Visa debit), `Dep`/`DEP`
 * (deposit), `ONLI` (online/Open24) and `BNOL`/`BONL` (business online). Per
 * @dylan219's card-dropdown spec, dropdown lists show ONLY debit (VSDR) and
 * deposit (Dep) cards and EXCLUDE the online card types (ONLI/BNOL). The mock
 * dataset carried friendly strings ("Debit"/"Online card") that masked this, so
 * the classifier also accepts those friendly forms.
 */
function normCardType(type?: string): string {
  return (type ?? '').trim().toUpperCase();
}

/** True for a deposit card (`Dep`/`DEP`, or the friendly "Deposit …"). */
function isDepositCard(type?: string): boolean {
  const t = normCardType(type);
  return t === 'DEP' || t.startsWith('DEPOSIT');
}

/**
 * Whether a card may appear in a card-selection dropdown (#114). Only Visa-debit
 * (VSDR) and deposit (Dep) cards are shown; the online card types ONLI and
 * BNOL/BONL are excluded entirely. The friendly mock strings ("Debit",
 * "Supplementary Debit", "Visa Debit") classify as debit; "Online card" / the
 * raw ONLI/BNOL codes are excluded.
 */
function isCardDropdownEligible(card: { Type?: string }): boolean {
  const t = normCardType(card.Type);
  if (!t) return false;
  // Online card types are never offered in a card dropdown (@dylan219, #114).
  if (t === 'ONLI' || t === 'BNOL' || t === 'BONL' || /ONLINE/.test(t))
    return false;
  if (isDepositCard(t)) return true;
  // Visa-debit codes / the friendly debit strings.
  return t === 'VSDR' || /DEBIT/.test(t);
}

/**
 * The agent-facing card label (#114, superseding #85's "{Card Type} {last 4}"):
 * @dylan219 wants the display name to be the card DESCRIPTION (the gateway
 * `CardDescription`, surfaced by the BFF adapter as `Description`), with the
 * deposit card shown as "Deposit Card". We append the last 4 digits per the
 * #85/#92 convention, e.g. "PER DEBIT CARD 1234" / "Deposit Card 9202". The
 * last-4 is the masked `CardLast4` (NEVER the full PAN — the full number is
 * never displayed, logged, or stored). Falls back to the raw `Type`/'—' when no
 * description is present so the label/column is never blank.
 */
function cardDescription(card: {
  Type?: string;
  Description?: string;
}): string {
  // A deposit card always reads "Deposit Card" regardless of the gateway's
  // (often blank/coded) description for that type.
  if (isDepositCard(card.Type)) return 'Deposit Card';
  const desc = (card.Description ?? '').trim();
  if (desc) return desc;
  // Defensive fallback when the gateway omits a description.
  return (card.Type ?? '').trim();
}

/** The full card label: "{Card Description} {last 4}", with a defensive fallback. */
function cardLabel(card: {
  Type?: string;
  Description?: string;
  CardLast4?: string;
}): string {
  const desc = cardDescription(card);
  const last4 = (card.CardLast4 ?? '').trim();
  if (desc && last4) return `${desc} ${last4}`;
  // Fallbacks so the label/column is never blank.
  return desc || card.Description || card.Type || '—';
}

/**
 * Formats a gateway date for display as `DD/MM/YYYY`. Tolerates BOTH the real
 * gateway's `DD/MM/YYYY` and the mock's ISO `YYYY-MM-DD` (#356), and renders a
 * `—` placeholder for an absent value or a `00/00/0000` / zero sentinel — so the
 * card table never shows the literal `00/00/0000` Dylan reported (#356).
 */
function formatDate(value?: string): string {
  const iso = toIsoDate(value);
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A scripting-completed / confirm-scripting checkbox (faithful port). */
function ScriptingCheckbox({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
        aria-checked={checked}
      />
      <label
        htmlFor={id}
        className="relative flex items-center cursor-pointer group"
      >
        <div className="relative w-[20px] h-[20px] border-2 transition-all bg-white flex-shrink-0 border-[#979c9f] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc] group-hover:border-[#1d252c]">
          {checked && (
            <svg
              className="absolute inset-0 m-auto size-[14px]"
              viewBox="0 0 10 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 4L3.5 6.5L9 1"
                stroke="#1d252c"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        <span className="ml-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
          {label}
        </span>
      </label>
    </div>
  );
}

/** The card + account detail table shown for the selected card (CCF-87). */
function CardTable({ card }: { card: CardRow }): React.JSX.Element {
  const headers = [
    'Description',
    'Card Number',
    'Status',
    // CCF-19/88/89 name these columns in the plural (@dylan219, #227).
    'Issue Dates',
    'Expiry Dates',
    'Account number',
    'Available Balance',
    'A/C Hold',
  ];
  return (
    <div className="overflow-x-auto mb-6 max-w-[1100px]">
      <table
        className="border-collapse bg-white"
        role="table"
        aria-label="Card details"
      >
        <thead>
          <tr className="bg-[#f3f4f6]">
            {headers.map((h) => (
              <th
                key={h}
                scope="col"
                className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border border-[#d1d5db]">
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {cardLabel(card)}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              **** **** **** {card.CardLast4}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {card.Status}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {formatDate(card.IssueDate)}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {formatDate(card.ExpiryDate)}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {card.AccountNumber}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {formatCurrency(card.AvailableBalance)}
            </td>
            <td className="px-4 py-3 border border-[#d1d5db] text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
              {card.AccountHold ?? 'N'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * The card-activation review table (CCF-20). Unlike the other actions, the
 * agent ticks the card's selection box here before scripting/activation
 * becomes available; the table adds an "Earliest Activation Date" column.
 */
function ActivationTable({
  card,
  checked,
  onToggle,
}: {
  card: CardRow;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const headers = [
    'Select card',
    'Card Number',
    'Status',
    'Issue Date',
    'Earliest Activation Date',
    'Expiry Date',
    'Account number',
    'Available Balance',
    'A/C Hold',
  ];
  const cell =
    'px-4 py-3 border border-[#d1d5db] text-[14px] font-["Arial",sans-serif] text-[#1d252c]';
  return (
    <div className="overflow-x-auto mb-6 max-w-[1100px]">
      <table
        className="border-collapse bg-white"
        role="table"
        aria-label="Card activation"
      >
        <thead>
          <tr className="bg-[#f3f4f6]">
            {headers.map((h) => (
              <th
                key={h}
                scope="col"
                className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border border-[#d1d5db]">
            <td className={cell}>
              <input
                type="checkbox"
                id={`activation-${card.CardToken}`}
                checked={checked}
                onChange={onToggle}
                className="sr-only peer"
                aria-checked={checked}
                aria-label={`Select card ${card.CardLast4} for activation`}
              />
              <label
                htmlFor={`activation-${card.CardToken}`}
                className="relative flex items-center cursor-pointer group"
              >
                <div className="relative w-[20px] h-[20px] border-2 transition-all bg-white flex-shrink-0 border-[#979c9f] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc] group-hover:border-[#1d252c]">
                  {checked && (
                    <svg
                      className="absolute inset-0 m-auto size-[14px]"
                      viewBox="0 0 10 8"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="#1d252c"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span className="ml-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                  {cardLabel(card)}
                </span>
              </label>
            </td>
            <td className={cell}>**** **** **** {card.CardLast4}</td>
            <td className={cell}>{card.Status}</td>
            <td className={cell}>{formatDate(card.IssueDate)}</td>
            <td className={cell}>{formatDate(card.EarliestActivationDate)}</td>
            <td className={cell}>{formatDate(card.ExpiryDate)}</td>
            <td className={cell}>{card.AccountNumber}</td>
            <td className={cell}>{formatCurrency(card.AvailableBalance)}</td>
            <td className={cell}>{card.AccountHold ?? 'N'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * The CIF + postal address panel with the secure-address field and Update
 * action (CCF-77). The agent flips Y↔N and clicks Update to commit the change.
 *
 * When the Secure Address flag is N we DISPLAY the issuing branch code to the
 * agent (#188, @dylan219 2026-07-01): it is the first 6 digits of the account
 * number after the PTSB prefix, DERIVED client-side from the card's account
 * number the screen already holds (`deriveIssuingBranchCode`) — the SAME method
 * used by the Chequebook journey. This is display-only: it is NOT a gateway
 * branch-lookup (#154 removed the eager fetch + "Could not load the issuing
 * branch" error — the branch code is derived, not fetched), and it NEVER
 * overwrites the postal address (#189 — the postal address is left untouched
 * when the flag is N).
 */
function AddressPanel({
  address,
  cifAddress,
  cifEircode,
  cifNumber,
  secureAddress,
  issuingBranch,
  onSecureAddressChange,
  onUpdate,
  result,
}: {
  address: PostalAddressResult | null;
  cifAddress: string[];
  cifEircode: string;
  cifNumber: string;
  secureAddress: string;
  /** The derived issuing branch code (first 6 digits after the PTSB prefix),
   * or undefined when it cannot be derived (#188). Shown only when the flag is N. */
  issuingBranch?: string;
  onSecureAddressChange: (value: string) => void;
  onUpdate: () => void;
  /** Inline result of the Update action (gateway Code+Message / confirmation). */
  result?: { ok: boolean; message: string } | null;
}): React.JSX.Element {
  return (
    <CustomerAddressPanel
      cifAddress={cifAddress}
      cifNumber={cifNumber}
      eircode={cifEircode}
      postalAddress={address?.PostalAddress ?? []}
      changeAddressPath="/personal-details"
      actions={
        <>
          <p className="text-[12px] font-['Arial',sans-serif] text-[#1d252c] m-0 max-w-[300px]">
            Check if eircode is populated in eircode field that it is also in
            Line 5 of CIF address
          </p>

          <hr className="border-t border-[#dad7d3] my-4" />

          <div className="flex items-center gap-3">
            <label
              htmlFor="secure-address"
              className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
            >
              Secure address:
            </label>
            <input
              id="secure-address"
              type="text"
              value={secureAddress}
              onChange={(e) =>
                onSecureAddressChange(e.target.value.toUpperCase())
              }
              maxLength={1}
              aria-label="Secure address"
              className="w-[60px] px-2 py-1.5 border border-[#d1d5db] bg-white font-['Arial',sans-serif] text-[14px] text-[#1d252c] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20"
            />
            <button
              type="button"
              onClick={onUpdate}
              className="px-6 py-2 bg-[#FF703E] text-[#1d252c] font-['Arial',sans-serif] font-bold text-[14px] rounded-md transition-all focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20 hover:bg-[#e55a2a]"
            >
              Update
            </button>
            {/* Inline action result beside the button (gateway Code+Message on
                failure, confirmation on success). */}
            {result && (
              <p
                role={result.ok ? 'status' : 'alert'}
                className={`text-[14px] font-['Arial',sans-serif] font-bold m-0 ${
                  result.ok ? 'text-[#1d252c]' : 'text-[#dc2626]'
                }`}
              >
                {result.message}
              </p>
            )}
          </div>

          {/* Issuing branch code shown only when Secure Address = N (#188):
              derived client-side from the account number (first 6 digits after
              the PTSB prefix), never a gateway lookup (#154), never folded into
              the postal address (#189). */}
          {secureAddress === 'N' && (
            <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mt-2">
              Issuing Branch:{' '}
              <span className="font-normal">{issuingBranch ?? '—'}</span>
            </p>
          )}
        </>
      }
    />
  );
}

/** Orange-bordered scripting info box (faithful port). */
function ScriptingBox({
  lines,
}: {
  lines: React.ReactNode[];
}): React.JSX.Element {
  return (
    <div className="mb-4 p-4 bg-[#F8EEE3] border-l-4 border-[#FF703E] max-w-max">
      <ul className="space-y-2 list-disc pl-5 m-0">
        {lines.map((line, i) => (
          <li
            key={i}
            className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
          >
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The inline status beside a replacement / PIN-reminder commit button reflecting
 * the 3-hold check (issue #149; #185 ruling). While the holds read is in flight
 * the action is withheld and a "checking" note shows; on a failed read the action
 * is BLOCKED and a retryable inline error is shown (fail-closed). On a code-3
 * hold (`has3`) the action is BLOCKED with the mandated advisory (@dylan219 #185
 * ruling 2026-07-01 — SUPERSEDES the earlier confirm-and-proceed popup): the
 * agent must remove the hold and retry; there is NO proceed. Nothing renders once
 * the read has cleared.
 */
function HoldGateNotice({
  status,
  onRetry,
}: {
  status: HoldStatus;
  onRetry: () => void;
}): React.JSX.Element | null {
  if (status === 'loading') {
    return (
      <p
        role="status"
        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
      >
        Checking account holds…
      </p>
    );
  }
  // Issue #695 — the code-3 (#185) AND code-N block advisories are now surfaced
  // as a MODAL pop-up when the agent ATTEMPTS the action (see
  // `CardHoldBlockPopup`), not as an inline notice. This inline notice therefore
  // only covers the loading + fail-closed read-error states; `has3`/`hasN`
  // render nothing inline (the pop-up carries the mandated wording + actions).
  if (status === 'error') {
    return (
      <p
        role="alert"
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0 inline-flex items-center gap-2"
      >
        Could not check the account for holds. The action is blocked until the
        check succeeds.
        <button
          type="button"
          onClick={onRetry}
          className="underline font-bold text-[#dc2626] focus:outline-none focus:ring-2 focus:ring-[#005fcc] rounded"
        >
          Retry
        </button>
      </p>
    );
  }
  return null;
}

/**
 * Issue #695 — the pre-API card-hold BLOCK pop-up (SCRN-49). A modal
 * (`role="alertdialog"`) shown when the agent attempts a Replace / PIN reminder
 * on an account carrying a `3` hold (rule 1) or an `N` hold (rules 2/3). It
 * carries the mandated @dylan219 wording and NO "confirm and proceed to API"
 * button — the action is BLOCKED. The 3-hold variant offers a "Recheck holds"
 * action (block-until-removed, #185 semantics); the N-hold variants are
 * acknowledge-only. Styled to match `JnHoldPopup` so scripted hold pop-ups stay
 * visually consistent.
 */
function CardHoldBlockPopup({
  kind,
  message,
  onClose,
  onRecheck,
}: {
  kind: 'has3' | 'hasN';
  message: string;
  onClose: () => void;
  /** Re-run a fresh holds read (3-hold only — block-until-removed, #185/#695). */
  onRecheck?: () => void;
}): React.JSX.Element {
  const title =
    kind === 'has3'
      ? '3 Hold on this account'
      : 'Account restriction — cannot replace card';
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="card-hold-popup-title"
      aria-describedby="card-hold-popup-message"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
    >
      <div className="bg-white border-2 border-[#dad7d3] shadow-lg max-w-[720px] w-full max-h-[90vh] overflow-auto p-8">
        <h2
          id="card-hold-popup-title"
          className="text-[20px] font-['Arial',sans-serif] font-bold m-0 mb-6 text-[#dc2626]"
        >
          {title}
        </h2>

        {/* The exact mandated advisory (@dylan219 #695). */}
        <p
          id="card-hold-popup-message"
          role="alert"
          className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-8 leading-[1.5] whitespace-pre-line"
        >
          {message}
        </p>

        <div className="flex justify-end gap-4">
          {kind === 'has3' && onRecheck && (
            <button
              type="button"
              onClick={onRecheck}
              aria-label="Recheck holds"
              className="px-6 py-3 bg-white text-[#1d252c] border-2 border-[#dad7d3] font-['Arial',sans-serif] font-bold text-[16px] rounded-md transition-all hover:border-[#979c9f] focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20"
            >
              Recheck holds
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="px-6 py-3 bg-[#FF703E] text-[#1d252c] border-2 border-[#FF703E] font-['Arial',sans-serif] font-bold text-[16px] rounded-md transition-all hover:bg-[#e55a2a] hover:border-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function CardMaintenanceScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const customerNumber = customer.cif ?? '';
  const customerName = customer.name ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  // Admin-editable card-service decline advisories (#637 / CCF-113, SCRN-49).
  // `useContent` is a hook, so resolve at component top level; it returns the
  // admin override OR the compiled default, so this map is always correct.
  // Terse technical codes stay compiled-in via CARD_SERVICE_CODE_REASON.
  const cardReasonOverrides: Record<string, string> = {
    '9145': useContent('card.activation.blocked'),
    '9147': useContent('card.activation.not_activatable'),
    '9148': useContent('card.activation.too_early'),
    '9184': useContent('card.pin.already_updated_today'),
  };

  // Resolved content-catalogue overrides for the Card Maintenance email
  // templates (#637 inc 24 — Gambling Block). Passed into the template context
  // so an admin reword of the `email.gambling_block.*` keys flows through to the
  // generated Outlook draft; with no override each template renders its
  // byte-identical compiled-in default.
  const contentMap = useContentMap();

  const [cards, setCards] = useState<CardRow[]>([]);
  // #851 (CR Change 3, CCRS-3893) — how the BFF scoped the card list to the
  // online card in context. `filtered` = a business (BONL) card in context, so
  // only cards on accounts that card reaches were returned; `unverified` = the
  // BFF could not verify the cards against it (§6.4): the dropdown is NOT
  // populated, an error is shown and the agent can retry. Never rendered as
  // an empty list.
  const [cardScope, setCardScope] = useState<
    'unfiltered' | 'filtered' | 'unverified'
  >('unfiltered');
  // Bumped by the §6.4 Retry to re-run the card load.
  const [cardsReload, setCardsReload] = useState(0);
  const [selectedCard, setSelectedCard] = useState(
    searchParams.get('card') ?? '',
  );
  // Latest selected card, readable from async callbacks (e.g. the Retry holds
  // read) without staling on the value captured at the time the read started —
  // used to discard a holds resolution that lands after a card switch (issue #149).
  const selectedCardRef = useRef(selectedCard);
  selectedCardRef.current = selectedCard;
  const [option, setOption] = useState<Option>('');
  const [error, setError] = useState('');

  // Address / secure-address (CCF-77) — loaded when an action is chosen.
  const [address, setAddress] = useState<PostalAddressResult | null>(null);
  const [secureAddress, setSecureAddress] = useState('Y');
  // Inline result of the secure-address Update action, surfaced beside the button
  // (the inline action-result UX convention): the gateway Code+Message on failure,
  // a clear confirmation on success.
  const [secureResult, setSecureResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  // CIF address — sourced from the customer dashboard (the real card/postal-address
  // wire does NOT carry it), mirroring StatementsScreen's loadCifAddress (#91).
  const [cifAddress, setCifAddress] = useState<string[]>([]);
  const [cifEircode, setCifEircode] = useState('');

  // Replacement-specific state (CCF-88/89).
  const [retainPin, setRetainPin] = useState<boolean | null>(null);
  const [waiveFee, setWaiveFee] = useState('');
  const [replacementFee, setReplacementFee] = useState<number | null>(null);
  const [chargeExempt, setChargeExempt] = useState(false);

  // Activation-specific selection (CCF-20): the agent must tick the card's row
  // before scripting/activation becomes available.
  const [activationSelected, setActivationSelected] = useState(false);

  const [scriptingDone, setScriptingDone] = useState(false);
  const [completed, setCompleted] = useState('');
  // Inline, recoverable action failure — surfaces the gateway Code + Message
  // beside the PIN-reminder / Activate / Replace button (role="alert"), a
  // non-blocking warning that lets the agent retry, per the gateway-result UX
  // convention (@dylan219 #80/#82/#94). Distinct from `completed` (success
  // confirmation) and the fail-closed 3-hold `HoldGateNotice`.
  const [actionError, setActionError] = useState('');

  // "3 hold" address-verification prompt (issue #149). When the selected card's
  // associated account carries a hold code `3`, committing a replacement
  // (CCF-88/89) or PIN-reminder (CCF-19) action must first interrupt the agent
  // with a confirm-only popup to verify the customer's address; on Confirm the
  // original action proceeds as normal. The hold flag is loaded from the
  // existing `/accounts/holds` route for the card's account (no new endpoint).
  //
  // The 3-hold check is a control that MUST fail CLOSED: the commit action is
  // withheld until the holds read resolves, and a failed/errored read BLOCKS the
  // action with a retryable inline error rather than silently proceeding (a
  // missed `3` hold would skip the mandated address verification). The status is
  // therefore tri-/quad-state, never a bare boolean defaulting to "no hold".
  //   loading — the holds read is in flight (action withheld)
  //   clear   — read succeeded, no `3`/`N` hold (action proceeds)
  //   has3    — read succeeded, `3` hold present (action BLOCKED, #185 ruling)
  //   hasN    — read succeeded, `N` hold ("Pay No Withdrawal") present (BLOCKED,
  //             #695 — the balance sign then picks the pop-up script)
  //   error   — read failed (action BLOCKED until a successful retry)
  //
  // Issue #695 (@dylan219, refining his own #185 ruling): the block is now
  // surfaced as a MODAL POP-UP when the agent ATTEMPTS the action (not only an
  // inline notice), and adds the `N`-hold rule. The 3-hold pop-up keeps the
  // #185 block-until-removed semantics (a "Recheck holds" action, no proceed);
  // the N-hold pop-up is an acknowledge-only block whose script differs by the
  // account balance sign (positive → contact-another-department; negative →
  // deposit-to-clear). Fail-closed read states are unchanged. `HoldStatus` is a
  // module-level type (above).
  const [holdStatus, setHoldStatus] = useState<HoldStatus>('idle');
  // Issue #695 — when the agent attempts an action while a `3`/`N` hold stands,
  // this holds the pop-up to render (null = no pop-up). The pop-up is opened by
  // the attempt (`guardHolds`); closing it dismisses it without any API call.
  const [holdPopup, setHoldPopup] = useState<'has3' | 'hasN' | null>(null);

  // #673 — `selectedCard` holds a per-ROW selection key (the card's position in
  // the loaded list), NOT its `CardToken`. The REAL gateway can return the SAME
  // `CardToken` for two DISTINCT physical cards under one account (observed on a
  // staging customer: an Activated debit card and an Unactivated debit card on
  // the same account BOTH carried an identical `CardToken`), so keying the
  // selector on the token collapsed both dropdown options onto one value —
  // `cards.find(c => c.CardToken === selectedCard)` always returned the FIRST
  // match and the second card was unreachable ("only lets us select one card").
  // A row-position key is unique per card row, so each card is independently
  // selectable and its own detail row / status renders; only the UI SELECTION
  // identity changes. Card ACTIONS still key on the resolved card's `CardToken`
  // (below) — the gateway's op identifier — so when two rows share one token the
  // action cannot target a specific card and is blocked (`tokenAmbiguous`).
  //
  // An empty / non-numeric key resolves to NO card (never accidentally the 0th
  // card via `Number('') === 0`).
  const selectedIndex = /^\d+$/.test(selectedCard) ? Number(selectedCard) : -1;
  const card = selectedIndex >= 0 ? cards[selectedIndex] : undefined;
  // The gateway card identifier for the selected row's action calls.
  const selectedCardToken = card?.CardToken ?? '';

  // #673 (ai-review) — every card ACTION (activate, PIN reminder, replacement,
  // secure-/postal-address) keys ONLY on the card's `CardToken` at the gateway
  // boundary: the R4 `card/*` request schemas (app/contracts/gateway/cards.r4.yaml)
  // identify the target card by `CardToken` and take the account-level Open24
  // `CardNumber` (the ID&V-bound card — SAME for every card on the account), NOT
  // the per-row masked PAN. There is NO per-card field on those requests that can
  // distinguish two physical cards that share one `CardToken`. So while the #673
  // display fix lets the agent SELECT either duplicate-token card, an ACTION on
  // one produces a byte-identical payload to an action on the other — the gateway
  // cannot tell which physical card the agent meant. Rather than risk acting on
  // the WRONG card, FAIL CLOSED: block state-changing actions for a card whose
  // `CardToken` is non-unique in the loaded list (the card stays viewable). A
  // blank token is not treated as a collision (blank means "no card resolved").
  const tokenAmbiguous =
    !!card &&
    selectedCardToken !== '' &&
    cards.filter((c) => c.CardToken === selectedCardToken).length > 1;

  // Issuing branch code shown when Secure Address = N (#188, @dylan219): the
  // first 6 digits of the account number after the PTSB prefix, DERIVED
  // client-side from the selected card's account number (the SAME method the
  // Chequebook journey uses). Display-only: it does NOT fetch a branch-lookup
  // endpoint (#154) and does NOT touch the postal address (#189).
  const issuingBranchCode = deriveIssuingBranchCode(card?.AccountNumber);

  // Load the customer's cards for the selector (CCF-87).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST('/cards', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load cards.');
          return;
        }
        const scope = data.OnlineCardScope?.status;
        if (scope === 'unverified') {
          // §6.4 — unverified cards are never displayed (fail closed).
          setCards([]);
          setSelectedCard('');
          setCardScope('unverified');
          return;
        }
        const list = (Array.isArray(data.Cards)
          ? data.Cards
          : []) as unknown as CardRow[];
        setCardScope(scope === 'filtered' ? 'filtered' : 'unfiltered');
        setCards(list);
        if (list.length === 0 && scope !== 'filtered') {
          toast.error('No cards available for maintenance.');
        }
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, cardsReload]);

  // Load the CIF address from the customer dashboard (the same detail set the
  // Personal Details screen reads) — the real `card/postal-address` wire does NOT
  // carry it (issue #91). Non-fatal: the CIF column renders blank lines on
  // failure, never blocking a card action. Mirrors StatementsScreen.loadCifAddress.
  useEffect(() => {
    let cancelled = false;
    async function loadCifAddress(): Promise<void> {
      // Clear any previous customer's CIF address FIRST so a stale address can
      // never render for a different customer (cross-customer PII). Every branch
      // below — no-context, apiError, throw, empty — then leaves it cleared until
      // a successful load for THIS customer repopulates it.
      setCifAddress([]);
      setCifEircode('');
      if (!hasContext) return;
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled || apiError || !data) return;
        const info = (data.info ?? {}) as Record<string, unknown>;
        const str = (v: unknown): string =>
          typeof v === 'string' ? v.trim() : '';
        setCifAddress([
          str(info.CustomerAddressLine1),
          str(info.CustomerAddressLine2),
          str(info.CustomerAddressLine3),
          str(info.CustomerAddressLine4),
          str(info.CustomerAddressLine5),
        ]);
        setCifEircode(str(info.CustomerEircode));
      } catch {
        // Non-fatal — the CIF address column renders blank lines when absent.
      }
    }
    void loadCifAddress();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  /** Resets all action state (called when card/option changes). */
  function resetAction(): void {
    setScriptingDone(false);
    setCompleted('');
    setActionError('');
    setRetainPin(null);
    setWaiveFee('');
    setReplacementFee(null);
    setChargeExempt(false);
    setActivationSelected(false);
    // Issue #695 — dismiss any open hold pop-up when the selection changes.
    setHoldPopup(null);
    // Fail CLOSED with NO window (issue #149): synchronously re-arm the hold gate
    // to a BLOCKING state in the SAME state update that changes the selection, so
    // a click immediately after a card/option change can never observe a STALE
    // `clear`/`has3` from the previously selected card before the post-render
    // effect re-runs the holds read. The effect then transitions loading → the
    // freshly-resolved status for the new selection.
    setHoldStatus('loading');
  }

  /**
   * Toggle the secure-address flag in the form. The change is committed on
   * Update (`updateSecureAddress`); the issuing branch is intentionally not
   * looked up or displayed here (#154 — per product it is not shown on this
   * screen).
   */
  function handleSecureAddressChange(value: string): void {
    const flag = value === 'N' ? 'N' : value === 'Y' ? 'Y' : value;
    setSecureAddress(flag);
  }

  /**
   * Issue #149 — read the selected card's associated account's holds and resolve
   * the tri-state `holdStatus` (loading → clear|has3|error). This is a fail-CLOSED
   * control: a failed read sets `error` (which BLOCKS the commit action behind a
   * retryable inline error), NOT `clear`. Extracted so the Retry control can re-run
   * it. Returns the cancelled-guarded resolution; the caller owns the loading set.
   */
  const loadHolds = React.useCallback(
    async (
      acctForHolds: string,
      isCancelled?: () => boolean,
    ): Promise<void> => {
      const stale = (): boolean => isCancelled?.() === true;
      if (!acctForHolds) {
        // No account on the card → nothing to gate on; treat as clear so the
        // action is never permanently withheld for a card with no account.
        if (!stale()) setHoldStatus('clear');
        return;
      }
      // Bound the holds read with a timeout (issue #173). A holds call that
      // never settles (a stalled gateway for a problematic account — the tester
      // saw the Replace button stuck forever on "Checking account holds…") must
      // not leave the action permanently withheld with no escape. We both abort
      // the in-flight request AND race it against an explicit timeout that fails
      // the read CLOSED to `error`, so the gate recovers (inline error + Retry)
      // even if the underlying request never settles. A symbol sentinel marks
      // the timeout branch unambiguously (vs any data the call might return).
      const controller = new AbortController();
      const TIMED_OUT = Symbol('holds-read-timeout');
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => {
          controller.abort();
          resolve(TIMED_OUT);
        }, HOLDS_READ_TIMEOUT_MS);
      });
      try {
        const outcome = await Promise.race([
          api.POST('/accounts/holds', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber, accountNumber: acctForHolds },
            signal: controller.signal,
          }),
          timeout,
        ]);
        if (stale()) return;
        if (outcome === TIMED_OUT) {
          // The read did not settle within the budget → fail CLOSED (block +
          // Retry) rather than leave the action stuck on "Checking account holds…".
          setHoldStatus('error');
          return;
        }
        const { data, error: apiError } = outcome;
        if (apiError || !data) {
          // A failed read must NOT be treated as "no hold" — block the action.
          setHoldStatus('error');
          return;
        }
        const result = data as unknown as {
          Holds?: { Code?: string }[];
          ReadOk?: boolean;
        };
        if (result.ReadOk === false) {
          // The gateway returned HTTP 200 but did NOT affirmatively succeed at
          // reading the holds (`SuccessErrorInd:'N'`, e.g. an unknown/invalid
          // account → "Unable to Process Transaction", #173). The empty holds in
          // that envelope are NOT a true "no hold", so fail CLOSED rather than
          // letting a gateway error silently bypass the address-verification gate.
          setHoldStatus('error');
          return;
        }
        const holds = result.Holds;
        if (!Array.isArray(holds)) {
          // A successful response whose `Holds` is missing/non-array is a
          // malformed control read — we cannot affirmatively conclude there is
          // no `3` hold, so fail CLOSED (block + Retry) rather than treating it
          // as "no hold". Only a valid array clears the gate.
          setHoldStatus('error');
          return;
        }
        // Issue #185 (code-3) then #695 (code-N, "Pay No Withdrawal").
        // Precedence: 3-hold FIRST, then N-hold (as the SCRN-49 rules require).
        const has3 = holds.some((h) => (h.Code ?? '').trim() === '3');
        const hasN = holds.some(
          (h) => (h.Code ?? '').trim().toUpperCase() === 'N',
        );
        setHoldStatus(has3 ? 'has3' : hasN ? 'hasN' : 'clear');
      } catch {
        // Network/throw/timeout-abort is a failed control read → fail CLOSED,
        // block the action (the inline error + Retry is shown).
        if (!stale()) setHoldStatus('error');
      } finally {
        clearTimeout(timer);
      }
    },
    [customerNumber, tellerId],
  );

  // Load the address + secure-address flag (and charge for replacements) when an
  // action is chosen (CCF-77/89).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext || !selectedCard || !option) return;
      // Issue #149: load the selected card's associated account's holds and flag
      // whether a `3` hold is present, so the commit step can interrupt with the
      // address-verification prompt. This is a fail-CLOSED control: while the read
      // is in flight the commit action is withheld (`loading`), and a failed read
      // BLOCKS it (`error`) with a retryable inline error — it never silently
      // proceeds. Reset to `loading` first so a previous card's status can never
      // leak onto a different card/account.
      setHoldStatus('loading');
      const acctForHolds = card?.AccountNumber ?? '';
      await loadHolds(acctForHolds, () => cancelled);
      if (cancelled) return;
      try {
        const { data } = await api.POST('/cards/postal-address', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, cardToken: selectedCardToken },
        });
        if (cancelled) return;
        const result = (data ?? null) as unknown as PostalAddressResult | null;
        setAddress(result);
        const flag = result?.SecureAddress ?? card?.SecureAddress ?? 'Y';
        setSecureAddress(flag);
      } catch {
        /* non-fatal: panel renders with empty lines */
      }
      if (option === 'lost-stolen') {
        try {
          const { data } = await api.POST('/cards/replacement-charge', {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: {
              customerNumber,
              cardToken: selectedCardToken,
              reason: 'LOST_STOLEN',
            },
          });
          if (cancelled) return;
          const charge = data as unknown as {
            ReplacementFee?: number;
            ChargeExempt?: string;
          };
          setReplacementFee(charge?.ReplacementFee ?? null);
          setChargeExempt(charge?.ChargeExempt === 'Y');
        } catch {
          /* non-fatal */
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, selectedCard, option]);

  async function updateSecureAddress(): Promise<void> {
    if (!card) return;
    setSecureResult(null);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/cards/secure-address', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          cardToken: selectedCardToken,
          secureAddress: secureAddress === 'N' ? 'N' : 'Y',
        },
      });
      if (apiError || !data) {
        // Issue #248 — an RBAC action denial (403 `{ error: 'forbidden' }`)
        // surfaces @dylan219's authoritative verbatim copy inline beside the
        // Update button, never a raw 403 or a generic error. Any other failure
        // (including the Teller-ID gate's `invalid_teller_id`) keeps the
        // existing distinct message.
        const message =
          resolveActionBlockMessage(response.status, apiError) ??
          'Could not update the secure address.';
        toast.error(message);
        setSecureResult({ ok: false, message });
        return;
      }
      // Surface the gateway's Code+Message inline on a not-OK result.
      const result = data as unknown as {
        Updated?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (result.Updated === false) {
        const code = result.ResultCode ? `${result.ResultCode} — ` : '';
        const msg = `${code}${result.ResultMessage ?? 'Could not update the secure address.'}`;
        toast.error(msg);
        setSecureResult({ ok: false, message: msg });
        return;
      }
      toast.success('Secure address updated.');
      setSecureResult({ ok: true, message: 'Secure address updated.' });
    } catch {
      toast.error('Request failed.');
      setSecureResult({ ok: false, message: 'Request failed.' });
    }
  }

  async function orderPinReminder(): Promise<void> {
    setActionError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/cards/pin-reminder', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, cardToken: selectedCardToken },
      });
      const result = data as unknown as {
        Ordered?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (apiError || !data || !result.Ordered) {
        // Surface the gateway Code + Message INLINE beside the button as a
        // recoverable warning (@dylan219 #80/#82/#94) — not only a fleeting toast.
        const inline = cardActionErrorText(
          response.status,
          result,
          'PIN reminder could not be ordered. Please retry.',
          apiError,
          cardReasonOverrides,
        );
        setActionError(inline);
        toast.error(inline);
        return;
      }
      setCompleted('PIN reminder ordered');
      toast.success('PIN reminder ordered');
    } catch {
      const inline = 'Could not order the PIN reminder. Please retry.';
      setActionError(inline);
      toast.error(inline);
    }
  }

  async function activateCard(): Promise<void> {
    setActionError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/cards/activate', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, cardToken: selectedCardToken },
      });
      const result = data as unknown as {
        Activated?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (apiError || !data || !result.Activated) {
        // Inline gateway Code + Message beside the Activate button (recoverable).
        const inline = cardActionErrorText(
          response.status,
          result,
          'Card could not be activated. Please retry.',
          apiError,
          cardReasonOverrides,
        );
        setActionError(inline);
        toast.error(inline);
        return;
      }
      setCompleted('The card has now been activated');
      toast.success('Card activation initiated');
    } catch {
      const inline = 'Could not activate the card. Please retry.';
      setActionError(inline);
      toast.error(inline);
    }
  }

  async function replaceCard(reason: 'DAMAGED' | 'LOST_STOLEN'): Promise<void> {
    // A replacement is a one-shot completion (CCF-88/89). Once it has succeeded,
    // refuse a repeat submission — the disabled button is the primary guard, this
    // is defence-in-depth against a duplicate call.
    if (completed) return;
    setActionError('');
    try {
      // #833 — the gateway REQUIRES an `EmbossName` on `card/replacement` for an
      // embossable card category, returning error 9168 otherwise. @dylan219's
      // authoritative rule: include the field only when the selected card's
      // description is "BUS DEBIT CARD" (normalised — case-insensitive, trimmed),
      // sourcing the value from the card-list `EmbossName` (gateway
      // `EmbossingName`). Do NOT send it for other card descriptions.
      const isBusDebit =
        cardDescription(card ?? {})
          .trim()
          .toUpperCase() === 'BUS DEBIT CARD';
      const embossName = ((card?.EmbossName ?? '') as string).trim();
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/cards/replace', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          cardToken: selectedCardToken,
          reason,
          // Damaged cards always retain the PIN (CCF-88); lost/stolen uses the
          // agent's selection (CCF-89).
          retainPin: reason === 'DAMAGED' ? true : retainPin === true,
          // Lost/stolen always sends the waive decision so the gateway never
          // receives an omitted WaiveCharge (#841, @dylan219). For a
          // charge-exempt card there is no fee to waive, so the moot question is
          // suppressed in the UI and we send WaiveCharge:'Y' (waiveFee:true);
          // otherwise we send the agent's Yes/No selection (blank → No).
          ...(reason === 'LOST_STOLEN'
            ? { waiveFee: chargeExempt ? true : waiveFee === 'Yes' }
            : {}),
          ...(isBusDebit && embossName ? { embossName } : {}),
        },
      });
      const result = data as unknown as {
        Replaced?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (apiError || !data || !result.Replaced) {
        // Inline gateway Code + Message beside the Replace button (recoverable).
        const inline = cardActionErrorText(
          response.status,
          result,
          'Card could not be replaced. Please retry.',
          apiError,
          cardReasonOverrides,
        );
        setActionError(inline);
        toast.error(inline);
        return;
      }
      setCompleted('Card has been replaced');
      toast.success('Card replacement initiated');
    } catch {
      const inline = 'Could not replace the card. Please retry.';
      setActionError(inline);
      toast.error(inline);
    }
  }

  /**
   * Issue #149 / #185 ruling — gate a replacement / PIN-reminder commit behind
   * the 3-hold check. This is a fail-CLOSED control: ONLY a freshly-resolved
   * `clear` (no code-3 hold) for the CURRENTLY selected card/account may proceed.
   * Every other state blocks (defence-in-depth — the button is also disabled):
   *   - `idle`    — no fresh holds read has resolved for the current selection;
   *     nothing runs. NEVER proceed on an unresolved state.
   *   - `loading` — the holds read is in flight; nothing runs.
   *   - `error`   — the holds read failed; BLOCKED (inline error + Retry shown).
   *   - `has3`    — a code-3 hold is present; BLOCKED with the mandated advisory
   *     (@dylan219 #185 ruling — SUPERSEDES the earlier confirm-and-proceed
   *     popup); nothing runs. The agent must remove the hold and retry.
   *   - `clear`   — the action runs.
   * The BFF ALSO re-checks holds and blocks server-side (api.ts `checkThreeHold`,
   * #173 pattern), so a direct route call cannot bypass this.
   */
  function guardHolds(action: () => void): void {
    // Fail closed: only a freshly-resolved `clear` for the current selection may
    // proceed. Issue #695 — a `3` hold (`has3`) or an `N` hold (`hasN`) BLOCKS
    // (no proceed) and the AGENT'S ATTEMPT surfaces the mandated MODAL pop-up
    // (@dylan219 — "when a user attempts these pieces before we trigger the
    // API"); the gateway op is NOT called. `idle`/`loading`/`error` also block
    // (the button is disabled in those states, so this is defence-in-depth).
    if (holdStatus === 'has3' || holdStatus === 'hasN') {
      setHoldPopup(holdStatus);
      return;
    }
    if (holdStatus !== 'clear') return;
    action();
  }

  /**
   * Re-run the holds read after a failed lookup (the inline Retry control, #149).
   * Selection-keyed: capture the card under retry and discard the resolution if
   * the agent switched cards mid-flight, so a stale retry can never apply its
   * result to a newer selection (which has already re-armed `loading`).
   */
  function retryHolds(): void {
    const retriedCard = selectedCard;
    // Issue #695 — the "Recheck holds" action (both the inline error Retry and
    // the 3-hold pop-up) closes the pop-up and re-runs a FRESH holds read. If the
    // hold has since been removed the read resolves `clear` and the action
    // re-enables; if it still stands, `guardHolds` re-opens the pop-up on the
    // next attempt (block-until-removed, #185).
    setHoldPopup(null);
    setHoldStatus('loading');
    void loadHolds(
      card?.AccountNumber ?? '',
      () => selectedCardRef.current !== retriedCard,
    );
  }

  // Issue #149 / #185 / #695 — the holds check gates the replacement /
  // PIN-reminder commit (fail CLOSED). The commit button is DISABLED in the
  // fail-closed READ states (`idle`/`loading`/`error`) — there, a click can do
  // nothing useful, so the control fails CLOSED with no window (a click right
  // after a card/option change, which re-arms `loading`, cannot commit).
  //
  // For a resolved `3`/`N` hold the button is DELIBERATELY left ENABLED so the
  // agent's ATTEMPT surfaces the mandated MODAL pop-up (@dylan219 #695 — "when a
  // user attempts these pieces before we trigger the API"); `guardHolds` opens
  // the pop-up and BLOCKS the gateway op (it never calls the action). Only a
  // freshly-resolved `clear` actually commits. The BFF ALSO re-checks + blocks
  // server-side (api.ts `cardPostingBlocked`, #173), so a direct route call
  // cannot bypass this.
  const holdReadPending =
    holdStatus === 'idle' || holdStatus === 'loading' || holdStatus === 'error';

  // Issue #173 (item 1, @dylan219 authoritative 2026-06-30) — on a lost/stolen
  // replacement, when the agent sets Waive Fee = "No" the customer is charged the
  // replacement fee; the order must be BLOCKED if the customer's available balance
  // is insufficient to cover that fee. The agent must either ensure sufficient
  // balance or switch the waiver to "Yes" (the fee is then waived, so no balance
  // is required). Ordering is allowed when the waiver is "Yes", when the customer
  // is charge-exempt (no fee at all), or when the balance covers the fee.
  //
  // The fee figure is the gateway `ReplacementFee` (the same value shown in the
  // "Advise customer of … charge" line); fall back to €5 — the displayed default —
  // when the charge call hasn't (yet) returned a figure, so the gate never blocks
  // on a €0/unknown fee. The available balance is the card's account balance.
  const effectiveReplacementFee = replacementFee ?? 5;
  const availableBalance = card?.AvailableBalance;
  const insufficientForFee =
    option === 'lost-stolen' &&
    !chargeExempt &&
    waiveFee === 'No' &&
    typeof availableBalance === 'number' &&
    availableBalance < effectiveReplacementFee;

  // Card-selector options (#114): show ONLY debit (VSDR) and deposit (Dep)
  // cards — ONLI/BNOL online card types are excluded from every card dropdown
  // (@dylan219). The value is the card's ROW POSITION in the loaded list (its
  // unique selection key — #673: the gateway can hand two distinct cards the
  // same `CardToken`, so the token is NOT a safe per-row identity), and the
  // label is the card DESCRIPTION + last 4 (e.g. "PER DEBIT CARD 1234" /
  // "Deposit Card 9202"). Preserving the original index keeps the key stable and
  // lets `cards[Number(key)]` resolve the exact row the agent picked.
  const cardOptions = cards
    .map((c, index) => ({ card: c, index }))
    .filter(({ card: c }) => isCardDropdownEligible(c))
    .map(({ card: c, index }) => ({
      value: String(index),
      label: cardLabel(c),
    }));

  // The customer HAS cards on file but none are eligible for the card-selector
  // (e.g. an online-only customer whose sole card is ONLI/BNOL — excluded from
  // every card dropdown per #114). Without an explicit message the dropdown is
  // silently empty, which reads as "broken" to the agent (#142). Surface a clear
  // note so the agent knows the customer simply has no maintainable card, rather
  // than guessing — distinct from the `cards.length === 0` ("no cards on file")
  // toast already raised on load.
  const hasOnlyIneligibleCards =
    cardScope !== 'filtered' && cards.length > 0 && cardOptions.length === 0;
  // #851 (CR §6.3) — a business (BONL) card in context and, once filtered to
  // the accounts it reaches, no card is left to maintain.
  const noCardsUnderOnlineCard =
    cardScope === 'filtered' && cardOptions.length === 0;

  // Maintenance options offered for the selected card, filtered by the card's
  // current status (CCF-19/20/88/89): a cancelled/closed card offers none; PIN
  // reminder is Active-only; activation is Unactivated + date-eligible only.
  const validOptions = card ? validOptionsFor(card) : [];
  // Keep a stable display order regardless of computed order.
  const optionOrder: Exclude<Option, ''>[] = [
    'lost-stolen',
    'damaged',
    'pin-reminder',
    'activation',
  ];
  const optionItems = optionOrder
    .filter((o) => validOptions.includes(o))
    .map((o) => ({ value: o, label: OPTION_LABEL[o] }));

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="card-maintenance-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Card Maintenance email templates (#472, mirroring #441/#442): the
            card-surface email templates (Gambling Block, Travel Spend, and
            Apple/Google Pay Token — Drive "SCRN 50") are consolidated into a
            single top-right dropdown selector in the page header, from which
            the agent picks one — consistent with every other screen that
            carries email templates. Each pops the source template prefilled
            best-effort from the in-context customer name, the customer CIF (for
            the Travel Spend robot's bare-number CIF line) and the selected
            card's last-4 (a full PAN is never rendered — the gateway masks it
            anyway); the per-template free-text choices are left for the agent to
            complete on the call. Nothing is sent from the app: the agent
            completes + sends the draft in Outlook. (Replaces the previous
            card-gated `EmailTemplateButtons` row — per issue #472.) */}
        <EmailTemplateMenu
          templates={CARD_MAINTENANCE_TEMPLATES}
          context={{
            customerName,
            cif: customerNumber,
            cardNumber: card?.CardLast4,
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="card-maintenance-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Card maintenance menu
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* #851 (CR §6.4) — the cards could not be verified against the
          business online card in context: the dropdown stays unpopulated and
          the agent can retry. */}
      {cardScope === 'unverified' && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] flex items-center justify-between gap-4 flex-wrap"
        >
          <span>
            Cards could not be verified against the online card in context, so
            none can be shown. Please retry.
          </span>
          <button
            type="button"
            onClick={() => {
              setCardScope('unfiltered');
              setCardsReload((n) => n + 1);
            }}
            className="underline font-bold text-[#dc2626] focus:outline-none focus:ring-2 focus:ring-[#005fcc] rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Card + maintenance-option selection (CCF-87). */}
      <div className="flex items-end gap-4 mb-6 flex-wrap">
        <div className="w-[300px]">
          <Dropdown
            id="card-select"
            label="Select card"
            placeholder="Select card"
            value={selectedCard}
            onChange={(val) => {
              setSelectedCard(val);
              setOption('');
              resetAction();
            }}
            options={cardOptions}
          />
        </div>

        {card && !tokenAmbiguous && optionItems.length > 0 && (
          <div className="w-[300px]">
            <Dropdown
              id="maintenance-option"
              label="Maintenance options"
              placeholder="Select option"
              value={option}
              onChange={(val) => {
                setOption(val as Option);
                resetAction();
              }}
              options={optionItems}
            />
          </div>
        )}
      </div>

      {selectedCard && !card && cards.length > 0 && (
        <p className="font-['Arial',sans-serif] text-[14px] text-[#6b7280]">
          Select a card to continue.
        </p>
      )}

      {/* The customer has cards on file but none are eligible for maintenance
          (online-only — ONLI/BNOL are excluded from card dropdowns, #114). Make
          the empty selector self-explanatory rather than silent (#142). */}
      {hasOnlyIneligibleCards && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[14px] text-[#6b7280]"
        >
          This customer has no debit or deposit cards eligible for maintenance.
          Online-only cards are not shown here.
        </p>
      )}

      {/* #673 (ai-review) — the selected card shares its `CardToken` with another
          card on this account, so no card-action request can uniquely target it
          (see `tokenAmbiguous`). Keep the card viewable but REFUSE maintenance
          actions, and tell the agent why + where to go instead. */}
      {tokenAmbiguous && (
        <p
          role="alert"
          className="font-['Arial',sans-serif] text-[14px] text-[#1d252c] p-4 bg-white border border-[#dc2626]"
        >
          This card cannot be uniquely identified for maintenance actions (it
          shares a card token with another card on this account). Please carry
          out this maintenance in Uniscreens, or contact support.
        </p>
      )}

      {/* #851 (CR §6.3) — every card was excluded by the business online
          card filter. */}
      {noCardsUnderOnlineCard && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[14px] text-[#6b7280]"
        >
          No cards are available for maintenance under this online card.
        </p>
      )}

      {/* A cancelled/closed card offers no maintenance actions
          (CCF-19/20/88/89 — no replace/maintenance on dead cards). */}
      {card && !tokenAmbiguous && optionItems.length === 0 && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[14px] text-[#6b7280]"
        >
          No maintenance actions are available for a {card.Status.toLowerCase()}{' '}
          card.
        </p>
      )}

      {/* --- Selected-card detail + actions (SCRN 50). --- */}
      {/* `!tokenAmbiguous` (#673 ai-review): a card whose token is shared with
          another card on the account cannot be uniquely targeted, so it exposes
          NO action panel — the inline alert above is shown instead. */}
      {card && !tokenAmbiguous && option && validOptions.includes(option) && (
        <section aria-label={OPTION_LABEL[option]}>
          {/* The activation flow uses its own review table with the
              Earliest Activation Date column + per-card tick-box (CCF-20);
              every other action uses the shared card-detail table. */}
          {option === 'activation' ? (
            <ActivationTable
              card={card}
              checked={activationSelected}
              onToggle={() => {
                setActivationSelected((s) => !s);
                // Unticking the card retracts the downstream scripting/action.
                setScriptingDone(false);
                setCompleted('');
              }}
            />
          ) : (
            <CardTable card={card} />
          )}

          {/* PIN reminder: card table → address → scripting → order (CCF-19). */}
          {option === 'pin-reminder' && (
            <>
              <AddressPanel
                address={address}
                cifAddress={cifAddress}
                cifEircode={cifEircode}
                cifNumber={customerNumber}
                secureAddress={secureAddress}
                onSecureAddressChange={handleSecureAddressChange}
                onUpdate={() => void updateSecureAddress()}
                result={secureResult}
                issuingBranch={issuingBranchCode}
              />
              <ScriptingBox
                lines={[
                  // CCF-19 scripting — verbatim to the story text (@dylan219, #227).
                  'If PIN is blocked, issuing a PIN reminder will unblock the PIN at midnight on working days.',
                  'If requested at the weekend, PIN will not unblock until Monday at midnight.',
                  'Please allow 3 to 5 working days for your PIN to arrive. This will also be available for you to view on the PTSB app.',
                ]}
              />
              <div className="mb-6">
                <ScriptingCheckbox
                  id="pin-scripting"
                  checked={scriptingDone}
                  onChange={() => setScriptingDone((s) => !s)}
                  label="Scripting Completed"
                />
              </div>
              {scriptingDone && (
                <div className="flex items-center gap-6 mb-6">
                  <button
                    type="button"
                    onClick={() => guardHolds(() => void orderPinReminder())}
                    disabled={!!completed || holdReadPending}
                    aria-disabled={!!completed || holdReadPending}
                    aria-label="Order PIN reminder"
                    className={ACTION_BUTTON_CLASS}
                  >
                    Order PIN reminder
                  </button>
                  <HoldGateNotice status={holdStatus} onRetry={retryHolds} />
                  {actionError && !completed && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
                    >
                      {actionError}
                    </p>
                  )}
                  {completed && (
                    <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                      {completed}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Card activation: tick card → scripting → activate (CCF-20).
              Scripting + the Activate button only appear once the agent has
              ticked the card's selection box in the activation table. */}
          {option === 'activation' && activationSelected && (
            <>
              <ScriptingBox
                lines={[
                  'You will need to make a Chip and PIN transaction before using your new card online or over the phone.',
                  'Once you have made a Chip and PIN transaction this will turn on the contactless feature of your card.',
                ]}
              />
              <div className="mb-6">
                <ScriptingCheckbox
                  id="activation-scripting"
                  checked={scriptingDone}
                  onChange={() => setScriptingDone((s) => !s)}
                  label="Scripting Completed"
                />
              </div>
              {scriptingDone && (
                <div className="flex items-center gap-6 mb-6">
                  <button
                    type="button"
                    onClick={() => void activateCard()}
                    disabled={!!completed}
                    aria-label="Activate card"
                    className={ACTION_BUTTON_CLASS}
                  >
                    Activate
                  </button>
                  {actionError && !completed && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
                    >
                      {actionError}
                    </p>
                  )}
                  {completed && (
                    <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                      {completed}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Replace as damaged: address → scripting → replace (CCF-88). */}
          {option === 'damaged' && (
            <>
              <AddressPanel
                address={address}
                cifAddress={cifAddress}
                cifEircode={cifEircode}
                cifNumber={customerNumber}
                secureAddress={secureAddress}
                onSecureAddressChange={handleSecureAddressChange}
                onUpdate={() => void updateSecureAddress()}
                result={secureResult}
                issuingBranch={issuingBranchCode}
              />
              <ScriptingBox
                lines={[
                  'Please allow up to 7 working days for your card to arrive and your PIN will stay the same.',
                  'Your old card will work up to 90 days or until a new card is activated.',
                  'Please dispose of your old card securely once you activate your new card.',
                  'You will need to update your card details with any subscription service provider or merchant who has card details saved.',
                  'Please allow up to 2 working days for your Apple/Google wallet to update with the new card details.',
                ]}
              />
              <div className="mb-6">
                <ScriptingCheckbox
                  id="damaged-scripting"
                  checked={scriptingDone}
                  onChange={() => setScriptingDone((s) => !s)}
                  label="Confirm scripting"
                />
              </div>
              {scriptingDone && (
                <div className="flex items-center gap-6 mb-6">
                  <button
                    type="button"
                    onClick={() =>
                      guardHolds(() => void replaceCard('DAMAGED'))
                    }
                    disabled={!!completed || holdReadPending}
                    aria-disabled={!!completed || holdReadPending}
                    aria-label="Replace card"
                    className={ACTION_BUTTON_CLASS}
                  >
                    Replace card
                  </button>
                  <HoldGateNotice status={holdStatus} onRetry={retryHolds} />
                  {actionError && !completed && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
                    >
                      {actionError}
                    </p>
                  )}
                  {completed && (
                    <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                      {completed}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Replace as lost/stolen: address → fee + PIN → scripting → replace
              (CCF-89). */}
          {option === 'lost-stolen' && (
            <>
              <AddressPanel
                address={address}
                cifAddress={cifAddress}
                cifEircode={cifEircode}
                cifNumber={customerNumber}
                secureAddress={secureAddress}
                onSecureAddressChange={handleSecureAddressChange}
                onUpdate={() => void updateSecureAddress()}
                result={secureResult}
                issuingBranch={issuingBranchCode}
              />

              <div className="flex gap-6 mb-6 flex-wrap max-w-[800px]">
                {/* Waive Fee — hidden when the customer is charge-exempt. */}
                {!chargeExempt && (
                  <div className="p-6 border-2 border-[#dad7d3] bg-white shadow-sm flex-1 min-w-[360px]">
                    <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
                      Waive Fee
                    </h3>
                    <div className="flex gap-6 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
                          Fees should only be waived if:
                        </p>
                        <ul className="space-y-1 list-disc pl-5 m-0">
                          <li className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            Balance less than €5
                          </li>
                          <li className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            Card retained by PTSB ATM
                          </li>
                          <li className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            Card not received by post
                          </li>
                        </ul>
                      </div>
                      <fieldset className="border-0 p-0 m-0 flex-1 min-w-[140px]">
                        <legend className="sr-only">
                          Select waive fee option
                        </legend>
                        <div className="flex items-center gap-6 mb-4">
                          {['Yes', 'No'].map((opt) => (
                            <label
                              key={opt}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="waive-fee"
                                value={opt}
                                checked={waiveFee === opt}
                                onChange={() => setWaiveFee(opt)}
                                aria-label={`Waive fee ${opt}`}
                              />
                              <span className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                                {opt}
                              </span>
                            </label>
                          ))}
                        </div>
                        {waiveFee === 'No' && (
                          <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                            Advise customer of{' '}
                            {formatCurrency(replacementFee ?? 5)} charge
                          </p>
                        )}
                      </fieldset>
                    </div>
                  </div>
                )}

                {/* Retain PIN (CCF-89). */}
                <div className="p-6 border-2 border-[#dad7d3] bg-white shadow-sm w-[240px]">
                  <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
                    Retain PIN
                  </h3>
                  <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">
                      Select retain PIN option
                    </legend>
                    <div className="flex items-center gap-6">
                      {[
                        { label: 'Yes', val: true },
                        { label: 'No', val: false },
                      ].map(({ label, val }) => (
                        <label
                          key={label}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="retain-pin"
                            checked={retainPin === val}
                            onChange={() => setRetainPin(val)}
                            aria-label={`Retain PIN ${label}`}
                          />
                          <span className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>

              {retainPin !== null && (
                <>
                  <ScriptingBox
                    lines={
                      retainPin
                        ? [
                            'Please allow up to 7 working days for your card to arrive and your PIN will stay the same.',
                            'You will need to update your card details with any subscription service provider or merchant who has card details saved.',
                            'Your Apple/Google wallet will be updated with your new card details immediately.',
                          ]
                        : [
                            'Please allow up to 7 working days for your card to arrive and up to 3 working days for your PIN to arrive.',
                            'You will need to update your card details with any subscription service provider or merchant who has card details saved.',
                            'Your Apple/Google wallet will be updated with your new card details immediately.',
                          ]
                    }
                  />
                  <div className="mb-6">
                    <ScriptingCheckbox
                      id="lost-scripting"
                      checked={scriptingDone}
                      onChange={() => setScriptingDone((s) => !s)}
                      label="Confirm scripting"
                    />
                  </div>
                </>
              )}

              {retainPin !== null && scriptingDone && (
                <div className="flex items-center gap-6 mb-6">
                  <button
                    type="button"
                    onClick={() => {
                      // Defence-in-depth: the button is already disabled when the
                      // balance is insufficient and the fee is not waived (#173).
                      if (insufficientForFee) return;
                      guardHolds(() => void replaceCard('LOST_STOLEN'));
                    }}
                    disabled={
                      !!completed || holdReadPending || insufficientForFee
                    }
                    aria-disabled={
                      !!completed || holdReadPending || insufficientForFee
                    }
                    aria-label="Replace card"
                    className={ACTION_BUTTON_CLASS}
                  >
                    Replace card
                  </button>
                  {/* Issue #173 — inline error beside the action when the order is
                      blocked for insufficient balance (Waive Fee = No). The agent
                      must ensure sufficient balance or waive the fee. */}
                  {insufficientForFee ? (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
                    >
                      Cannot be ordered: insufficient available balance to cover
                      the replacement fee. Waive the fee or ensure sufficient
                      balance.
                    </p>
                  ) : (
                    <HoldGateNotice status={holdStatus} onRetry={retryHolds} />
                  )}
                  {actionError && !insufficientForFee && !completed && (
                    <p
                      role="alert"
                      className="text-[14px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0"
                    >
                      {actionError}
                    </p>
                  )}
                  {completed && (
                    <p className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                      {completed}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Issue #695 — the pre-API hold-check BLOCK pop-up (rules 1/2/3). Shown
          when the agent attempts a Replace / PIN reminder on an account carrying
          a `3` hold (rule 1, block-until-removed with a Recheck action) or an
          `N` hold (rules 2/3, acknowledge-only; the script differs by balance
          sign). No "proceed to API" action — the gateway op is never called. */}
      {holdPopup && (
        <CardHoldBlockPopup
          kind={holdPopup}
          message={
            holdPopup === 'has3'
              ? CARD_THREE_HOLD_POPUP_MESSAGE
              : // Rule 2 vs 3 — a genuinely NEGATIVE balance uses the
                // deposit-to-clear script; balance of exactly 0 or an undefined
                // balance with an N hold falls to the ">€0" positive-balance
                // script (@dylan219 #695 — the negative script applies only to a
                // real negative balance).
                // Issue #173: prefer AccountBalance (signed, from
                // customer/accounts/list) over AvailableBalance because the real
                // gateway always returns AvailableBalance >= 0 for overdrawn
                // accounts (0.00 for negative balances). Fall back to
                // availableBalance when AccountBalance is not present.
                (
                    typeof card?.AccountBalance === 'number'
                      ? card.AccountBalance < 0
                      : typeof availableBalance === 'number' &&
                        availableBalance < 0
                  )
                ? nHoldNegativeMessage(
                    customerName,
                    typeof card?.AccountBalance === 'number'
                      ? card.AccountBalance
                      : (availableBalance ?? 0),
                  )
                : nHoldPositiveMessage(customerName)
          }
          onClose={() => setHoldPopup(null)}
          onRecheck={holdPopup === 'has3' ? retryHolds : undefined}
        />
      )}
    </main>
  );
}
